import { isNil } from "@kalodata/tools";
import { CURRENCY_SYMBOL } from "@src/config/country";
import { formatKMB } from "@utils/util";
import { type PublishPerformance } from "./types";

const NON_NUMERIC_FALLBACK = "--";

/** Revenue：多币种不一致时 gmvAmount 为 null，无法聚合，展示 "--" 而非 0 以免误导 */
const formatRevenue = (amount?: string, currency?: string): string => {
  if (isNil(amount)) return NON_NUMERIC_FALLBACK;
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) return NON_NUMERIC_FALLBACK;
  const symbol = currency ? (CURRENCY_SYMBOL[currency] ?? "") : "";
  return `${symbol}${formatKMB(numeric, 2)}`;
};

/** 数字类指标：缺失补 0 */
const formatCount = (value?: number): string => {
  if (isNil(value)) return "0";
  return formatKMB(value, 0) ?? "0";
};

/** CTR：clickThroughRate 为比例（如 0.15 → 15%），缺失补 0% */
const formatCtr = (rate?: string): string => {
  if (isNil(rate)) return "0%";
  const numeric = Number(rate);
  if (!Number.isFinite(numeric)) return "0%";
  return `${parseFloat((numeric * 100).toFixed(2))}%`;
};

export interface SalesMetric {
  key: string;
  label: string;
  value: string;
}

/**
 * 按设计稿（5617:32076）顺序输出销售指标。
 * 当前仅 Revenue / Orders / Total Units / CTR 有后端字段，
 * GPM / Impressions / Clicks 待后端补充后再加入。
 */
export const buildSalesMetrics = (
  performance: PublishPerformance | undefined,
  t: (key: string) => string,
): SalesMetric[] => [
  { key: "revenue", label: t("Revenue"), value: formatRevenue(performance?.gmvAmount, performance?.gmvCurrency) },
  { key: "orders", label: t("Orders"), value: formatCount(performance?.orderCount) },
  { key: "totalUnits", label: t("Total Units"), value: formatCount(performance?.itemSoldCount) },
  { key: "ctr", label: t("CTR"), value: formatCtr(performance?.clickThroughRate) },
];
